import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Notification } from "@/components/dashboard/NotificationsPanel";

interface UseRealtimeSubscriptionProps {
  user: any;
  pages: any[] | undefined;
  onNewNotification: (notification: Notification) => void;
}

export const useRealtimeSubscription = ({
  user,
  pages,
  onNewNotification,
}: UseRealtimeSubscriptionProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  // Immediate invalidation with smart refetching
  const invalidateQueries = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Invalidate all dashboard data queries regardless of parameters
      queryClient.invalidateQueries({ 
        queryKey: ["dashboard-data"],
        refetchType: "active" // Only refetch queries currently in use
      });
      
      // Invalidate feedback pages to update submission counts
      queryClient.invalidateQueries({ 
        queryKey: ["feedback-pages"],
        refetchType: "active"
      });
      
      // Invalidate submission counts
      queryClient.invalidateQueries({ 
        queryKey: ["submission-counts"],
        refetchType: "active"
      });
    }, 100);
  }, [queryClient]);

  useEffect(() => {
    if (!user || !pages || pages.length === 0) return;

    const pageIds = pages.map((p) => p.id);

    const channel = supabase
      .channel("feedback-changes")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "feedback_submissions",
          filter: `page_id=in.(${pageIds.join(",")})`,
        },
        (payload) => {
          const newSubmission = payload.new as any;
          const page = pages.find((p) => p.id === newSubmission.page_id);

          const newNotification: Notification = {
            id: `notif-${Date.now()}`,
            type: "new_feedback",
            title: "New Feedback Received",
            message: newSubmission.content.substring(0, 100) + "...",
            timestamp: new Date().toISOString(),
            read: false,
            pageTitle: page?.title,
            pageId: newSubmission.page_id,
          };

          onNewNotification(newNotification);

          toast({
            title: "💬 New Feedback",
            description: `New feedback on ${page?.title || "your page"}`,
          });

          // Check for negative sentiment
          if (newSubmission.sentiment === "negative") {
            const alertNotification: Notification = {
              id: `alert-${Date.now()}`,
              type: "sentiment_alert",
              title: "Negative Feedback Alert",
              message: "You received negative feedback that may need attention",
              timestamp: new Date().toISOString(),
              read: false,
              pageTitle: page?.title,
              pageId: newSubmission.page_id,
            };
            onNewNotification(alertNotification);
          }

          // Invalidate queries to refresh dashboard
          invalidateQueries();
        }
      )
      .subscribe();

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      supabase.removeChannel(channel);
    };
  }, [user, pages, invalidateQueries, onNewNotification, toast]);
};
